<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ElPopover, ElInput, ElMessageBox, ElMessage } from 'element-plus'
import { Editor, EditorContent } from '@tiptap/vue-3';
import { useI18n } from '@/hooks/web/useI18n';
import StarterKit from '@tiptap/starter-kit';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import UnderLine from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
// import Image from '@tiptap/extension-image';
import CustomImage from './CustomImage';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { MarkdownSerializer, defaultMarkdownSerializer } from 'prosemirror-markdown';
import { MergeTableCells } from './MergeTableCells';
import { SplitTableCell } from './SplitTableCell';
import Indent from './Indent';
import Video from './Video';
import IFrame from './IFrame';
import { calcFileSizeStr, getBase64 } from '@/utils';
const { t } = useI18n();
const emit = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: {type: String,default: ''},
  maxImgSize: {type: Number,default: 0},
})

const editor = ref<any>(null)
const refImgSelector = ref<any>(null)
const refHeadingPopover = ref<any>(null)
const refTablePopover = ref<any>(null)
const tableRowCount = ref<any>(2)
const tableColCount = ref<any>(2)
const refImgPopover = ref<any>(null)
const imgWidth = ref<any>('')
const imgHeight = ref<any>('')
const refVideoPopover = ref<any>(null)
const videoWidth = ref<any>('100%')
const videoHeight = ref<any>('')
const videoUrl = ref<any>('')
const refIFramePopover = ref<any>(null)
const iframeWidth = ref<any>('100%')
const iframeHeight = ref<any>('')
const iframeUrl = ref<any>('')
const refTextColorPopover = ref<any>(null)
const textColor = ref<any>('')
const refTextHighlightPopover = ref<any>(null)
const highlightColor = ref<any>('#FFFF00')
const refTextAlignPopover = ref<any>(null)
const alignOptions = ref<any>([
  { text: t('fullText.alignLeft'), value: 'left' },
  { text: t('fullText.alignCenter'), value: 'center' },
  { text: t('fullText.alignRight'), value: 'right' },
  { text: t('fullText.alignJustify'), value: 'justify' },
])
const headers = ref<any>(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

watch(
  () => editor.value?.state.selection.empty,
  (newValue, oldValue) => {
    // 当 editor.state.selection.empty 变化时触发
    console.log('Selection empty changed:', newValue);
  },
  { immediate: true }
)

onMounted(() => {
  // 初始化 TipTap 编辑器
  editor.value = new Editor({
    extensions: [
      StarterKit,
      Subscript,
      Superscript,
      UnderLine,
      Highlight.configure({
        multicolor: true, // 可设置多色背景
      }),
      Table.configure({
        resizable: true, // 是否可调整列宽
      }),
      TableRow,
      TableCell,
      TableHeader,
      MergeTableCells,
      SplitTableCell,
      // Image.configure({
      //   inline: true,
      //   allowBase64: true,
      // }),
      CustomImage.configure({
        inline: true,
        allowBase64: true,
      }),
      Link,
      Video,
      IFrame,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'list_item', 'title'], // 允许的node类型
      }),
      Indent,
    ],
    content: '',
  })

  editor.value.on('update', () => {
    const html = editor.value?.getHTML()
    // console.log('Editor Content update:', html)
    emit('update:modelValue', html)
  })
  editor.value?.on('focus', ({ editor }) => {
    console.log('Editor focused:', editor);
  })
  editor.value?.on('blur', ({ editor }) => {
    console.log('Editor blured:', editor);
  })
})

onBeforeUnmount(() => {
  // 在组件销毁时销毁编辑器实例
  editor.value?.destroy()
})

const getTextContent = () => {
  if (!editor.value) return ''
  const content = document.getElementsByClassName('ProseMirror')[0]
  if (!content || !content.textContent) return ''
  return content.textContent
}
const getHtmlContent = () => {
  if (!editor.value) return ''
  return editor.value?.getHTML()
}
const getMarkdownContent = () => {
  if (!editor.value) return ''
  const state = editor.value.state; // 获取编辑器状态
  // console.log('defaultMarkdownSerializer.nodes:', defaultMarkdownSerializer.nodes);
  // console.log('defaultMarkdownSerializer.marks:', defaultMarkdownSerializer.marks);
  const serializer = new MarkdownSerializer(
    {
      ...defaultMarkdownSerializer.nodes,
      bulletList: defaultMarkdownSerializer.nodes.bullet_list,
      listItem: defaultMarkdownSerializer.nodes.list_item,
      orderedList: defaultMarkdownSerializer.nodes.ordered_list,
      codeBlock: defaultMarkdownSerializer.nodes.code_block,
      horizontalRule: defaultMarkdownSerializer.nodes.horizontal_rule,
      hardBreak: defaultMarkdownSerializer.nodes.hard_break,
      image: (state, node) => {
        const alt = node.attrs.alt ? `[${node.attrs.alt}]` : '';
        const title = node.attrs.title ? ` "${node.attrs.title}"` : '';
        const src = node.attrs.src || '';
        const width = node.attrs.width || '';
        const height = node.attrs.height || '';
        // Markdown 格式的图片：`![alt](src "title")`
        state.write(`![${alt}](${src}${title}){width=${width || 'auto'} height=${height || 'auto'}}\n`); // 添加换行符
      },
      video: (state, node) => {
        const src = node.attrs.src || '';
        const width = node.attrs.width || '640';
        const height = node.attrs.height || '360';

        // 1. 输出 Markdown 的标准链接格式
        state.write(`[Video Preview](${src})\n\n`);

        // 2. 如果 Markdown 渲染器支持 HTML，直接序列化为 HTML 标签
        state.write(
          `<video src="${src}" width="${width}" height="${height}" controls preload="metadata">\n`
        );
      },
      iframe: (state, node) => {
        const src = node.attrs.src || '';
        const width = node.attrs.width || '100%';
        const height = node.attrs.height || '315px';

        // 生成 Markdown 中的 HTML 标签表示 iframe
        state.write(`<iframe src="${src}" width="${width}" height="${height}" frameborder="0" allowfullscreen></iframe>\n`);
      },
      table: (state, node) => {
        let isHeaderProcessed = false; // 标记是否已处理表头
        node.forEach((row) => {
          if (!isHeaderProcessed) {
            // 渲染表头及分割线
            state.render(row);
            const columnCount = row.childCount;
            state.write('| ' + '--- | '.repeat(columnCount).trim() + '\n');
            isHeaderProcessed = true;
          } else {
            state.render(row);
          }
        });
      },
      tableRow: (state, node) => {
        state.write('| ');
        node.forEach((cell, _, i) => {
          if (i > 0) state.write(' | ');
          state.render(cell);
        });
        state.write(' |\n');
      },
      tableCell: (state, node) => {
        const content = node.textContent.trim();
        const colspan = node.attrs?.colspan || 1; // 获取列合并数
        const rowspan = node.attrs?.rowspan || 1; // 获取行合并数

        if (colspan > 1 || rowspan > 1) {
          // 在 Markdown 中添加自定义注释标记
          state.write(
            `<!-- colspan=${colspan} rowspan=${rowspan} --> ${content}`
          );
        } else {
          state.text(content);
        }
      },
      tableHeader: (state, node) => {
        state.write('**');
        state.text(node.textContent.trim());
        state.write('**');
      },
    },
    {
      ...defaultMarkdownSerializer.marks,
      bold: {
        open: '<strong>', //加粗
        close: '</strong>',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      italic: {
        open: '<em>', // Markdown 斜体开始标记
        close: '</em>', // Markdown 斜体结束标记
        mixable: true, // 可与其他标记混用
        expelEnclosingWhitespace: true, // 自动去掉空格
      },
      underline: {
        open: '<u>', // 用 HTML 标签 `<u>` 表示下划线的开始
        close: '</u>', // 用 HTML 标签 `</u>` 表示下划线的结束
        mixable: true, // 允许和其他标记混合
        expelEnclosingWhitespace: true, // 自动移除包裹的空格
      },
      strike: {
        open: '<s>', //删除线
        close: '</s>',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      highlight: {
        open: '<mark>', // 使用 HTML `<mark>` 标签表示高亮
        close: '</mark>',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      subscript: {
        open: '<sub>', // 使用 HTML `<sub>` 标签表示下标
        close: '</sub>',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      superscript: {
        open: '<sup>', // 使用 HTML `<sup>` 标签表示上标
        close: '</sup>',
        mixable: true,
        expelEnclosingWhitespace: true,
      },
      textStyle: {
        open(state, mark) {
          const { color } = mark.attrs;
          if (color) {
            // 使用 HTML 的 span 标签并添加样式
            return `<span style="color: ${color}">`;
          }
          return ''; // 如果没有颜色样式，返回空字符串
        },
        close() {
          return '</span>'; // 关闭 span 标签
        },
        mixable: true,
        expelEnclosingWhitespace: true,
      },
    }
  )

  // 序列化编辑器内容为 Markdown
  return serializer.serialize(state.doc);
}
const generateTocData = (headingSelector = 'h1, h2, h3, h4, h5, h6') => {
    const content = document.getElementsByClassName('ProseMirror')[0]
    // console.log(content)
    const headings = Array.from(content.querySelectorAll(headingSelector));

    const tocData = [];
    const stack:any = [{ level: 0, children: tocData }]; // 用于构造层级结构

    headings.forEach((heading:any) => {
      const level = parseInt(heading.tagName[1], 10); // 获取标题层级 (h1 -> 1, h2 -> 2, ...)
      const item:any = {
          id: heading.id || heading.innerText.trim().toLowerCase().replace(/\s+/g, '-'),
          text: heading.innerText.trim(),
          level,
          children: []
      };
      heading.id = item.id; // 确保每个标题都有唯一的 ID
      // 将标题插入到正确的层级中
      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop(); // 找到合适的父级
      }
      stack[stack.length - 1].children.push(item);
      stack.push(item); // 当前标题入栈
    });

    return tocData;
}

const setHeading = (e, level:any) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleHeading({ level: level+1 }).run()
  refHeadingPopover.value?.hide()
}
const fontColor = (e, color) => {
  e.preventDefault()
  if (color) editor.value?.commands.setColor(color)
  else editor.value?.commands.unsetColor()
  refTextColorPopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const underline = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleUnderline().run()
}
const highlight = (e, color) => {
  e.preventDefault()
  if (color) editor.value?.commands.toggleHighlight({ color: color })
  else editor.value?.commands.toggleHighlight()
  refTextHighlightPopover.value?.hide()
}
const bold = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleBold().run()
}
const italic = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleItalic().run()
}
const strike = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleStrike().run()
}
const superscript = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleSuperscript().run()
}
const subscript = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleSubscript().run()
}
const unset = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().unsetAllMarks().run()
}
const indent = (e) => {
  e.preventDefault()
  editor.value?.commands.indent()
}
const outdent = (e) => {
  e.preventDefault()
  editor.value?.commands.outdent()
}
const bulletList = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleBulletList().run()
}
const orderedList = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleOrderedList().run()
}
const textAlign = (e, align) => {
  e.preventDefault()
  editor.value?.commands.setTextAlign(align)
  refTextAlignPopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const codeBlock = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().toggleCodeBlock().run()
}
const tableCellSelected = computed(() => {
  if (!editor.value) return true
  const { selection } = editor.value.state;
  const { from, to } = selection;
  let selectedCells:any = []
  editor.value.state.doc.nodesBetween(from, to, (node) => {
    // console.log(node.type.name)
    if (node.type.name === 'tableCell') {
      selectedCells.push(node.type.name)
    }
  })
  return selectedCells.length>0
})
const tableSelected = computed(() => {
  if (!editor.value) return true
  const { selection } = editor.value.state;
  const { from, to } = selection;
  let selectedCells:any = []
  editor.value.state.doc.nodesBetween(from, to, (node) => {
    // console.log(node.type.name)
    if (node.type.name === 'table') {
      selectedCells.push(node.type.name)
    }
  })
  return selectedCells.length>0
})
const inserTable = (e) => {
  e.preventDefault()
  if (tableRowCount.value > 10) tableRowCount.value = 10
  if (tableColCount.value > 10) tableColCount.value = 10
  editor.value?.commands.insertTable({
    rows: tableRowCount.value,
    cols: tableColCount.value,
    withHeaderRow: false, // 是否包含表头行
  })
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const deleteTable = (e) => {
  e.preventDefault()
  editor.value?.commands.deleteTable();
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const mergeCells = (e) => {
  e.preventDefault()
  editor.value?.commands.mergeTableCells();
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const splitCells = (e) => {
  e.preventDefault()
  editor.value?.commands.splitTableCell();
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const AddRowAfter = (e) => {
  e.preventDefault()
  editor.value?.commands.addRowAfter()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const AddRowBefore = (e) => {
  e.preventDefault()
  editor.value?.commands.addRowBefore()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const AddColumnAfter = (e) => {
  e.preventDefault()
  editor.value?.commands.addColumnAfter()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const AddColumnBefore = (e) => {
  e.preventDefault()
  editor.value?.commands.addColumnBefore()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const deleteColumn = (e) => {
  e.preventDefault()
  editor.value?.commands.deleteColumn()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const deleteRow = (e) => {
  e.preventDefault()
  editor.value?.commands.deleteRow()
  refTablePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const link = (e) => {
  e.preventDefault()
  let options:any = {
    confirmButtonText: t('common.confirm'),
    cancelButtonText: t('common.cancel'),
    inputPattern:
      /^.+$/,
    inputErrorMessage: `${t('common.NotNull')}`,
    beforeClose: async (action, instance, done) => {
      // console.log(action, instance)
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        editor.value?.commands.setLink({ href: instance.inputValue })
        instance.confirmButtonLoading = false
        done()
      } else done()
    }
  }
  ElMessageBox.prompt('', t('fullText.link'), options).then(({ value }) => {
    nextTick(() => {
      editor.value?.view.focus()
    })
  }).catch(() => {})
}
const uploadImage = (e) => {
  e.preventDefault()
  refImgPopover.value?.hide()
  refImgSelector.value?.click()
}
const imgFileChange = async (e:any) => {
  const file = e.target.files[0]
  const limitType = ['png','jpg','jpeg','bmp','svg']
  const fileType = file.name.split('.').pop()
  if (!limitType.includes(fileType)) {
    ElMessage.error(`${t('common.fileFmtInvalid')};${t('common.supportTypes')}:${limitType.join(',')}`)
    refImgSelector.value.value = null
    return
  }
  if (props.maxImgSize > 0 && file.size > props.maxImgSize) {
    ElMessage.error(`${t('common.uploadSizeExceeded')}:${calcFileSizeStr(props.maxImgSize)}`)
    refImgSelector.value.value = null
    return
  }
  const base64 = await getBase64(file)
  editor.value?.commands.setImage({
    src: base64,
    width: imgWidth.value,
    height: imgHeight.value,
  })
  refImgSelector.value.value = null
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const setImgUrl = (e) => {
  e.preventDefault()
  refImgPopover.value?.hide()
  let options:any = {
    confirmButtonText: t('common.confirm'),
    cancelButtonText: t('common.cancel'),
    inputPattern:
      /^.+$/,
    inputErrorMessage: `${t('common.NotNull')}`,
    inputType: 'textarea',
    beforeClose: async (action, instance, done) => {
      // console.log(action, instance)
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        editor.value?.commands.setImage({
          src: instance.inputValue,
          width: imgWidth.value,
          height: imgHeight.value,
        })
        instance.confirmButtonLoading = false
        done()
      } else done()
    }
  }
  ElMessageBox.prompt('', 'URL', options).then(({ value }) => {
    nextTick(() => {
      editor.value?.view.focus()
    })
  }).catch(() => {})
}
const insertVideo = (e) => {
  e.preventDefault()
  if (!videoUrl.value) return
  editor.value?.chain().focus().setVideo({
    src: videoUrl.value, // 视频地址
    width: videoWidth.value, // 视频宽度
    height: videoHeight.value, // 视频高度
    controls: true, // 显示控制条
  }).run()
  videoUrl.value = ''
  refVideoPopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const insertIframe = (e) => {
  e.preventDefault()
  if (!iframeUrl.value) return
  editor.value?.chain().focus().setIFrame({
    src: iframeUrl.value,
    width: iframeWidth.value,
    height: iframeHeight.value,
  }).run()
  iframeUrl.value = ''
  refIFramePopover.value?.hide()
  nextTick(() => {
    editor.value?.view.focus()
  })
}
const undoDisable = () => {
  return !editor.value?.can().undo()
}
const redoDisable = () => {
  return !editor.value?.can().redo()
}
const undo = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().undo().run()
}
const redo = (e) => {
  e.preventDefault()
  editor.value?.chain().focus().redo().run()
}
const beforeLeave = () => {
  document.activeElement?.blur()
}

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    // console.log('Rich text modelValue change:', newValue)
    if (editor.value && editor.value.getHTML() !== newValue) {
      editor.value?.commands.setContent(newValue)
    }
  },
  {
    immediate: true
  }
)

defineExpose({
  getTextContent,
  getHtmlContent,
  getMarkdownContent,
  generateTocData
})
</script>

<template>
  <div class="rich-text-editor-wrapper">
    <!-- 自定义工具栏 -->
    <div class="rich-text-toolbar">
      <!-- 标题 -->
      <ElPopover
        ref="refHeadingPopover"
        placement="bottom"
        trigger="click"
        :width="50"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.title')" keep>
              <div class="icon-24px icon-header"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div
            v-for="item,index in headers" :key="index"
            @click.stop="setHeading($event, index)"
            class="popover-options-item">
            {{item}}
          </div>
        </template>
      </ElPopover>
      <!-- 文字颜色 -->
      <ElPopover
        ref="refTextColorPopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.txtColor')" keep>
              <div class="icon-24px icon-font-color"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div>
            <div @click.stop="fontColor($event, textColor)" class="popover-options-item">{{ t('fullText.setColor') }}</div>
            <!-- <div style="padding: 4px">
              <ElInput v-model.trim="textColor" @keyup.enter="fontColor($event, textColor)" style="width:80px" :placeholder="''" />
            </div> -->
            <div style="padding:4px;text-align:center;">
              <ElInput type="color" v-model.trim="textColor" @keyup.enter="fontColor($event, textColor)" style="width:48px" :placeholder="''" />
            </div>
          </div>
        </template>
      </ElPopover>
      <!-- 下划线 -->
      <CTooltip :tips="t('fullText.underline')" keep>
        <div @click.stop="underline" class="icon-24px icon-underline"></div>
      </CTooltip>
      <!-- 文字高亮 -->
      <ElPopover
        ref="refTextHighlightPopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.highlight')" keep>
              <div class="icon-24px icon-highlight"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div>
            <div @click.stop="highlight($event, highlightColor)" class="popover-options-item">{{ t('fullText.setColor') }}</div>
            <!-- <div style="padding: 4px">
              <ElInput v-model.trim="highlightColor" @keyup.enter="highlight($event, highlightColor)" style="width:80px" :placeholder="''" />
            </div> -->
            <div style="padding:4px;text-align:center;">
              <ElInput type="color" v-model.trim="highlightColor" @keyup.enter="highlight($event, highlightColor)" style="width:48px" :placeholder="''" />
            </div>
          </div>
        </template>
      </ElPopover>
      <!-- 加粗 -->
      <CTooltip :tips="t('fullText.bold')" keep>
        <div @click.stop="bold" class="icon-24px icon-bold"></div>
      </CTooltip>
      <!-- 斜体 -->
      <CTooltip :tips="t('fullText.italic')" keep>
        <div @click.stop="italic" class="icon-24px icon-italic"></div>
      </CTooltip>
      <!-- 删除线 -->
      <CTooltip :tips="t('fullText.strike')" keep>
        <div @click.stop="strike" class="icon-24px icon-strike"></div>
      </CTooltip>
      <!-- 上标 -->
      <CTooltip :tips="t('fullText.superscript')" keep>
        <div @click.stop="superscript" class="icon-24px icon-superscript"></div>
      </CTooltip>
      <!-- 下标 -->
      <CTooltip :tips="t('fullText.subscript')" keep>
        <div @click.stop="subscript" class="icon-24px icon-subscript"></div>
      </CTooltip>
      <div class="divider"></div>
      <!-- 清除所有标记 -->
      <CTooltip :tips="t('fullText.unsetAllMarks')" keep>
        <div @click.stop="unset" class="icon-24px icon-unset-all-marks"></div>
      </CTooltip>
      <!-- 缩进 -->
      <CTooltip :tips="t('fullText.indent')" keep>
        <div @click.stop="indent" class="icon-24px icon-indent"></div>
      </CTooltip>
      <!-- 反缩进 -->
      <CTooltip :tips="t('fullText.outdent')" keep>
        <div @click.stop="outdent" class="icon-24px icon-outdent"></div>
      </CTooltip>
      <!-- 无序列表 -->
      <CTooltip :tips="t('fullText.bulletList')" keep>
        <div @click.stop="bulletList" class="icon-24px icon-bullet-list"></div>
      </CTooltip>
      <!-- 有序列表 -->
      <CTooltip :tips="t('fullText.orderedList')" keep>
        <div @click.stop="orderedList" class="icon-24px icon-ordered-list"></div>
      </CTooltip>
      <!-- 文字对齐 -->
      <ElPopover
        ref="refTextAlignPopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.align')" keep>
              <div class="icon-24px icon-align-justify"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div
            v-for="item,index in alignOptions" :key="index"
            @click.stop="textAlign($event, item.value)"
            class="popover-options-item">
            {{item.text}}
          </div>
        </template>
      </ElPopover>
      <div class="divider"></div>
      <CTooltip :tips="t('fullText.codeBlock')" keep>
        <div @click.stop="codeBlock" class="icon-24px icon-code-block"></div>
      </CTooltip>
      <!-- 表格 -->
      <ElPopover
        ref="refTablePopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.table')" keep>
              <div class="icon-24px icon-table"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div>
            <div @click.stop="inserTable" class="popover-options-item">{{ t('fullText.insertTable') }}</div>
            <div class="insert-table-params-area">
              <ElInput v-model.trim="tableRowCount" style="width:50px" :placeholder="t('fullText.tableRow')" />
              <ElInput v-model.trim="tableColCount" style="width:50px" :placeholder="t('fullText.tableColumn')" />
            </div>
          </div>
          <div v-if="tableCellSelected" @click.stop="AddRowAfter" class="popover-options-item">{{ t('fullText.addRowAfter') }}</div>
          <div v-if="tableCellSelected" @click.stop="AddRowBefore" class="popover-options-item">{{ t('fullText.addRowBefore') }}</div>
          <div v-if="tableCellSelected" @click.stop="AddColumnAfter" class="popover-options-item">{{ t('fullText.addColumnAfter') }}</div>
          <div v-if="tableCellSelected" @click.stop="AddColumnBefore" class="popover-options-item">{{ t('fullText.addColumnBefore') }}</div>
          <div v-if="tableCellSelected" @click.stop="deleteColumn" class="popover-options-item">{{ t('fullText.deleteColumn') }}</div>
          <div v-if="tableCellSelected" @click.stop="deleteRow" class="popover-options-item">{{ t('fullText.deleteRow') }}</div>
          <div v-if="tableSelected" @click.stop="deleteTable" class="popover-options-item">{{ t('fullText.deleteTable') }}</div>
          <!-- <div v-if="tableCellSelected" @click.stop="mergeCells" class="popover-options-item">Merge Cells</div> -->
          <!-- <div v-if="tableCellSelected" @click.stop="splitCells" class="popover-options-item">Split Cells</div> -->
        </template>
      </ElPopover>
      <!-- 超链接 -->
      <CTooltip v-if="!editor?.state.selection.empty" :tips="t('fullText.link')" keep>
        <div @click.stop="link" class="icon-24px icon-link"></div>
      </CTooltip>
      <!-- 图片 -->
      <ElPopover
        ref="refImgPopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.image')" keep>
              <div class="icon-24px icon-image"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div class="insert-table-params-area">
            <div>{{ t('fullText.width') }}</div>
            <ElInput v-model.trim="imgWidth" style="width:100px" :placeholder="'px'" />
            <div>{{ t('fullText.height') }}</div>
            <ElInput v-model.trim="imgHeight" style="width:100px" :placeholder="'px'" />
          </div>
          <div @click.stop="uploadImage" class="popover-options-item">{{ t('fullText.upload') }}</div>
          <div @click.stop="setImgUrl" class="popover-options-item">{{ 'URL' }}</div>
        </template>
      </ElPopover>
      <!-- 视频 -->
      <ElPopover
        ref="refVideoPopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="t('fullText.video')" keep>
              <div class="icon-24px icon-video"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div>
            <div class="insert-table-params-area !pt-12px !pb-8px">
              <div>{{ t('fullText.width') }}</div>
              <ElInput v-model.trim="videoWidth" style="width:100px" :placeholder="'px'" />
              <div>{{ t('fullText.height') }}</div>
              <ElInput v-model.trim="videoHeight" style="width:100px" :placeholder="'px'" />
            </div>
            <div class="px-8px py-8px">
              <ElInput v-model.trim="videoUrl" @keyup.enter="insertVideo" :rows="3" type="textarea" style="width:300px" :placeholder="'URL'" />
            </div>
            <div class="flex px-8px py-6px" style="border-top:1px solid var(--c-border)">
              <Button @click.stop="insertVideo" type="primary" style="margin-left:auto">
                <div>{{t('common.confirm')}}</div>
              </Button>
            </div>
          </div>
        </template>
      </ElPopover>
      <!-- Iframe -->
      <ElPopover
        ref="refIFramePopover"
        placement="bottom"
        trigger="click"
        :teleported="false"
        :popper-style="{padding:'0px'}"
        @before-leave="beforeLeave">
        <template #reference>
          <div>
            <CTooltip :tips="'iframe'" keep>
              <div class="icon-24px icon-iframe"></div>
            </CTooltip>
          </div>
        </template>
        <template #default>
          <div>
            <div class="insert-table-params-area !pt-12px !pb-8px">
              <div>{{ t('fullText.width') }}</div>
              <ElInput v-model.trim="iframeWidth" style="width:100px" :placeholder="'px'" />
              <div>{{ t('fullText.height') }}</div>
              <ElInput v-model.trim="iframeHeight" style="width:100px" :placeholder="'px'" />
            </div>
            <div class="px-8px py-8px">
              <ElInput v-model.trim="iframeUrl" @keyup.enter="insertIframe" type="textarea" style="width:300px" :placeholder="'URL'" />
            </div>
            <div class="flex px-8px py-6px" style="border-top:1px solid var(--c-border)">
              <Button @click.stop="insertIframe" type="primary" style="margin-left:auto">
                <div>{{t('common.confirm')}}</div>
              </Button>
            </div>
          </div>
        </template>
      </ElPopover>
      <div class="divider"></div>
      <input @change="imgFileChange" ref="refImgSelector" type="file" accept="image/*" class="hidden" />
      <div style="height:24px;width:2px;background-color:var(--c-border)"></div>
      <!-- 撤销 -->
      <CTooltip :tips="t('fullText.undo')" keep>
        <div @click.stop="undo" class="icon-24px" :class="undoDisable()?'icon-undo-disable':'icon-undo'"></div>
      </CTooltip>
      <!-- 恢复 -->
      <CTooltip :tips="t('fullText.redo')" keep>
        <div @click.stop="redo" class="icon-24px" :class="redoDisable()?'icon-redo-disable':'icon-redo'"></div>
      </CTooltip>
    </div>
    <EditorContent :editor="editor" style="flex:1;overflow-y:auto;"/>
  </div>
</template>

<style lang="scss" scoped>
:deep(.ProseMirror) {
  @for $i from 1 through 7 /* max-indent */ {
    $indent-margin-base: 2rem;

    *[data-indent='#{$i}'] {
      margin-left: $indent-margin-base * $i !important;
    }
  }
}
</style>
<style lang="less" scoped>
.rich-text-editor-wrapper {
  height: 100%;
  border: 1px solid var(--c-border);
  display: flex;
  flex-direction: column;
  .rich-text-toolbar {
    flex-basis: 40px;
    border-bottom: 1px solid var(--c-border);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 8px;
    padding: 0px 12px;
    .divider {
      height: 24px;
      width: 1px;
      background-color: var(--c-border);
    }
  }
  :deep(.ProseMirror) {
    padding: 0.5rem;
    
    &-focused {
      outline-width: 0;
    }
    ul {
      all: revert;
    }
    ol {
      all: revert;
    }
    h1,h2,h3,h4,h5,h6 {
      all: revert;
    }
    h1 {
      font-size: 2em;
    }
    a {
      all: revert;
      color: var(--c-primary);
      cursor: pointer;
    }
    em {
      all: revert;
    }
    pre {
      background-color: var(--c-bg);
      color: var(--c-txt);
      font-size: 16px;
      overflow-x: auto;
      padding: 14px 20px;
      margin: 10px 0;
      border-radius: 5px;
      code {
        background-color: transparent;
        border-radius: 0;
        color: inherit;
        display: block;
        font-family: "Menlo,Monaco,Consolas,Courier,monospace";
        font-size: inherit;
        font-weight: 400;
        padding: 0;
      }
    }
    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      margin: 0;
      overflow: hidden;
    }
    th,td {
      border: 2px solid #ebeef5;
      text-align: center;
    }
    th.selectedCell, td.selectedCell {
      background-color: #ecf5ff;
    }
    sup {
      all: unset;
      vertical-align: super;
      font-size: smaller;
      line-height: inherit;
    }
    sub {
      all: unset;
      vertical-align: sub;
      font-size: smaller;
      line-height: inherit;
    }
  }
  :deep(.el-popover.el-popper) {
    width: auto !important;
    min-width: 60px !important;
  }
  .icon-24px {
    width: 24px;
    height: 24px;
    cursor: pointer;
    user-select: none;
  }
  .icon-header {
    content: url('@/assets/icons/icon-header.svg');
  }
  .popover-options-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
    color: var(--c-txt);
    cursor: pointer;
    border-bottom: 1px solid var(--c-border);
    font-size: 16px;
    font-weight: 700;
    &:hover {
      color: var(--c-primary);
    }
  }
  .insert-table-params-area {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 4px;
    border-bottom: 1px solid var(--c-border);
    padding: 4px;
  }
  .icon-font-color {
    content: url('@/assets/icons/icon-font-color.svg');
  }
  .icon-bold {
    content: url('@/assets/icons/icon-toggle-bold.svg');
  }
  .icon-italic {
    content: url('@/assets/icons/icon-toggle-italic.svg');
  }
  .icon-underline {
    content: url('@/assets/icons/icon-toggle-underline.svg');
  }
  .icon-strike {
    content: url('@/assets/icons/icon-toggle-strike.svg');
  }
  .icon-highlight {
    content: url('@/assets/icons/icon-toggle-highlight.svg');
  }
  .icon-toggle-link {
    content: url('@/assets/icons/icon-toggle-link.svg');
  }
  .icon-subscript {
    content: url('@/assets/icons/icon-toggle-subscript.svg');
  }
  .icon-superscript {
    content: url('@/assets/icons/icon-toggle-superscript.svg');
  }
  .icon-bullet-list {
    content: url('@/assets/icons/icon-bullet-list.svg');
  }
  .icon-ordered-list {
    content: url('@/assets/icons/icon-ordered-list.svg');
  }
  .icon-indent {
    content: url('@/assets/icons/icon-indent.svg');
  }
  .icon-outdent {
    content: url('@/assets/icons/icon-outdent.svg');
  }
  .icon-align-justify {
    content: url('@/assets/icons/icon-align-justify.svg');
  }
  .icon-code-block {
    content: url('@/assets/icons/icon-code-block.svg');
  }
  .icon-table {
    content: url('@/assets/icons/icon-table.svg');
  }
  .icon-unset-all-marks {
    content: url('@/assets/icons/icon-unset-all-marks.svg');
  }
  .icon-undo {
    content: url('@/assets/icons/icon-undo.svg');
  }
  .icon-redo {
    content: url('@/assets/icons/icon-redo.svg');
  }
  .icon-undo-disable {
    content: url('@/assets/icons/icon-undo-disable.svg');
    pointer-events: none;
  }
  .icon-redo-disable {
    content: url('@/assets/icons/icon-redo-disable.svg');
    pointer-events: none;
  }
  .icon-link {
    content: url('@/assets/icons/icon-full-text-link.svg');
  }
  .icon-image {
    content: url('@/assets/icons/icon-full-text-image.svg');
  }
  .icon-video {
    content: url('@/assets/icons/icon-video.svg');
  }
  .icon-iframe {
    content: url('@/assets/icons/icon-iframe.svg');
  }
}
</style>